// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { genesisLogisticsRoutes } from '../../config/marketplaceContent';
import { useTheme } from '../../context/ThemeContext';
import { fetchNoticeboardFeed } from '../../util/api';
import { requestProgressiveAuth } from '../../util/authIntent';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import GigStatusBadge from '../GigStatusBadge/GigStatusBadge';
import Web3WalletAuthPanel from '../../containers/AuthenticationPage/Web3WalletAuthPanel/Web3WalletAuthPanel';

import css from './LivePipelineTicker.module.css';

type TickerItem = {
  id: string;
  route: string;
  amount: string;
  status: string;
  clickable: boolean;
};

const statusLabel = (status: string) => {
  const normalized = String(status || '').toUpperCase();
  if (normalized === 'SETTLED' || normalized.startsWith('SETTLED_')) {
    return 'SETTLED';
  }
  return normalized;
};

const LivePipelineTicker: React.FC<{ isAuthenticated?: boolean }> = ({ isAuthenticated = false }) => {
  const { isDark } = useTheme();
  const history = useHistory();
  const location = useLocation();
  const routeConfiguration = useRouteConfiguration();
  const [items, setItems] = useState<TickerItem[]>([]);
  const [walletModalOpen, setWalletModalOpen] = useState(false);

  const loadItems = useCallback(() => {
    return fetchNoticeboardFeed()
      .then((gigs: any[]) => {
        if (!Array.isArray(gigs) || gigs.length === 0) {
          return genesisLogisticsRoutes.map(route => ({
            id: route.id,
            route: `${route.origin} ➔ ${route.destination}`,
            amount: route.amountUsdc,
            status: route.status,
            clickable: true,
          }));
        }
        return gigs.slice(0, 8).map(gig => ({
          id: gig.notice_id,
          route: gig.payload?.title || 'Active logistics lane',
          amount: String(gig.payload?.escrow_amount ?? '0'),
          status: 'FUNDED',
          clickable: true,
        }));
      })
      .catch(() =>
        genesisLogisticsRoutes.map(route => ({
          id: route.id,
          route: `${route.origin} ➔ ${route.destination}`,
          amount: route.amountUsdc,
          status: route.status,
          clickable: true,
        }))
      );
  }, []);

  useEffect(() => {
    loadItems().then(setItems);
    const intervalId = window.setInterval(() => {
      loadItems().then(setItems);
    }, 8000);
    return () => window.clearInterval(intervalId);
  }, [loadItems]);

  const doubled = useMemo(() => [...items, ...items], [items]);

  const handleItemClick = (noticeId: string) => {
    requestProgressiveAuth({
      history,
      routeConfiguration,
      location,
      intent: 'claimGig',
      userType: 'provider',
      onAuthenticated: () => setWalletModalOpen(true),
    });
  };

  return (
    <>
      <section
        className={isDark ? css.root : css.rootLight}
        aria-label="Active pipeline ticker"
        data-testid="live-pipeline-ticker"
      >
        <div className={css.label}>Live pipeline</div>
        <div className={css.track}>
          <div className={css.scroll}>
            {doubled.map((item, index) => (
              <button
                key={`${item.id}-${index}`}
                type="button"
                className={css.chip}
                onClick={() => handleItemClick(item.id)}
              >
                <span className={css.route}>{item.route}</span>
                <span className={css.separator}>|</span>
                <span className={css.amount}>{item.amount} USDC</span>
                <span className={css.separator}>|</span>
                <GigStatusBadge status={statusLabel(item.status)} />
              </button>
            ))}
          </div>
        </div>
      </section>

      {walletModalOpen ? (
        <div className={css.modalOverlay} role="dialog" aria-modal="true">
          <div className={css.modal}>
            <h3 className={css.modalTitle}>Link your Web3 Wallet to Start Transacting</h3>
            <p className={css.modalBody}>
              Connect MetaMask or Rabby and sign the linking message to interact with live gigs.
            </p>
            <Web3WalletAuthPanel onAuthenticated={() => setWalletModalOpen(false)} />
            <button type="button" className={css.modalClose} onClick={() => setWalletModalOpen(false)}>
              Close
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default LivePipelineTicker;
